import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Icon } from '../Icon';
import { LoadingIcon } from '../LoadingIcon/LoadingIcon';
import type { IconName, IconSize } from '../Icon';
import { cn } from '@/utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'large' | 'medium' | 'small';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  /** Optional icon rendered to the right of the label. */
  icon?: IconName;
}

// Icon sizes track button sizes: large→large, medium→large, small→medium
const iconSizeMap: Record<ButtonSize, IconSize> = {
  large: 'large',
  medium: 'large',
  small: 'medium',
};

// ─── Variant styles ───────────────────────────────────────────────────────────
// Each variant defines default, hover, and disabled states.
//
// OPEN QUESTION (design team): pressed/active state treatment on web.
//
// The design system's token scale is: quiet → moderate → loud → hover.
// There is no dedicated "pressed" token. On mobile this is not an issue —
// the pressed state maps to the hover token because there is no hover on touch.
// On web, hover and active fire in sequence (hover while the cursor is over the
// button, active only during the mousedown), so using the same token for both
// means the button does not change colour when clicked — there is no tactile
// visual feedback of the press. Three approaches to consider:
//
//   A. Hover only, no active styling — keeps hover affordance, no press feedback.
//   B. Active only, no hover styling — matches mobile behaviour, loses hover preview.
//   C. Separate tokens — hover uses `hover` token, active uses a darker value
//      (e.g. a raw palette step) — most feedback, but requires a new design token
//      or a one-off value outside the token system.
//
// Currently implemented as option A (hover only) until the design team decides.
// Remove this comment and update the styles once a direction is confirmed.
//
// Disabled styles use flat bg-disabled / text-disabled colours (not opacity) to
// match the Figma "inactive" state. The browser suppresses hover rules on
// :disabled elements automatically.

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    'bg-interactive-loud text-white border-transparent',
    'hover:bg-interactive-hover',
    'disabled:bg-disabled disabled:text-disabled disabled:border-transparent',
  ].join(' '),

  secondary: [
    'bg-surface text-interactive border border-interactive-loud',
    'hover:bg-interactive-quiet hover:border-interactive-hover hover:text-interactive-hover-text',
    'disabled:bg-disabled disabled:text-disabled disabled:border-default',
  ].join(' '),

  tertiary: [
    'bg-transparent text-interactive border-transparent',
    'hover:text-interactive-hover-text',
    'disabled:bg-transparent disabled:text-disabled disabled:border-transparent',
  ].join(' '),
};

// ─── Size styles ──────────────────────────────────────────────────────────────
// Each entry covers three concerns:
//   1. Padding  — py/px values from the Primer spacing scale
//   2. Radius   — border-radius token per size (large=medium, medium=small, small=xs)
//   3. Typography — .button-* component classes from tokens/tailwind.css (@layer components)
//                   which set font-family (Gilroy), font-size, font-weight (600),
//                   and letter-spacing.
//
// Icon-only variants (no children) use square padding so the button renders as
// a square: px is collapsed to match py. Defined separately to avoid a runtime
// CSS variable lookup.

const sizeStyles: Record<ButtonSize, string> = {
  large: 'py-4 px-6 rounded-medium button-large',
  medium: 'py-3 px-4 rounded-small button-medium',
  small: 'py-3 px-3 rounded-xs button-small',
};

const iconOnlySizeStyles: Record<ButtonSize, string> = {
  large: 'py-4 px-4 rounded-medium button-large',
  medium: 'py-3 px-3 rounded-small button-medium',
  small: 'py-3 px-3 rounded-xs button-small',
};

// ─── Button ───────────────────────────────────────────────────────────────────

/**
 * Primer Button component.
 *
 * Use `variant` to communicate action importance:
 * - `primary`   — the main action on a screen. Use once per view.
 * - `secondary` — a supporting action; outlined, surface background.
 * - `tertiary`  — minimal weight; text-only, no background or border.
 *
 * `forwardRef` is used so parent components can hold a ref to the underlying
 * `<button>` DOM node when needed — for example, to anchor a Tooltip or
 * Popover, to programmatically manage focus (e.g. returning focus after a
 * modal closes), or to integrate with third-party libraries that require a
 * DOM ref (e.g. Floating UI, Radix primitives).
 *
 * @see {@link https://www.figma.com/file/8Ea1vOzcKJlNpwAjraMtA0/Influenster-Design-System|Figma Design}
 * @see src/primer/README.md
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'large',
    isLoading = false,
    fullWidth = false,
    icon,
    disabled,
    className = '',
    children,
    ...props
  },
  ref
) {
  const isDisabled = disabled || isLoading;
  const hasChildren = children != null && children !== false && children !== '';

  const classes = cn(
    // Base — layout, transitions, cursor
    'inline-flex items-center justify-center border',
    'transition-colors duration-150',
    // Focus ring — a single consistent ring colour is used across all variants
    // for predictable keyboard navigation. The interactiveloud token (purple)
    // is the primary interactive colour in the design system and provides
    // sufficient contrast on both light and dark backgrounds.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'focus-visible:ring-(--border-color-interactiveloud)',
    'cursor-pointer disabled:cursor-not-allowed',
    // Variant (default, hover, disabled colours — see open question above re: active/pressed)
    variantStyles[variant],
    // Size (padding, radius, typography) — icon-only collapses to square padding
    hasChildren || !icon ? sizeStyles[size] : iconOnlySizeStyles[size],
    // Layout
    fullWidth && 'w-full',
    // Consumer overrides
    className
  );

  return (
    <button ref={ref} className={classes} disabled={isDisabled} {...props}>
      {isLoading ? (
        <>
          <span aria-hidden="true" className="inline-flex shrink-0">
            <LoadingIcon size={iconSizeMap[size]} />
          </span>
          {hasChildren && <span className="ml-2">{children}</span>}
        </>
      ) : (
        <>
          {hasChildren && <span>{children}</span>}
          {icon && (
            <span aria-hidden="true" className={cn('inline-flex shrink-0', hasChildren && 'ml-2')}>
              <Icon name={icon} size={iconSizeMap[size]} />
            </span>
          )}
        </>
      )}
    </button>
  );
});

export default Button;
